import axios from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import type {
  CustomFieldDefinition,
  Contact,
  Organization,
  Deal,
  PaymentSchedule,
  SavePaymentSchedulePayload,
  Pipeline,
  CloseReason,
  Note,
  Activity,
  BoardResponse,
  PaginatedResponse,
  Visitor,
  VisitorDetail,
  PageView,
  FormSubmission,
  AiReviewResult,
  QualifyingQuestion,
  ProposalDocument,
  MeetingNote,
  WebhookEndpoint,
  WebhookDelivery,
  InboundWebhookToken,
  HubspotImport,
  HubspotImportPreview,
  IntegrationConnection,
  HubspotDeal,
  HubspotContact as HubspotContactType,
  HubspotCompany as HubspotCompanyType,
  HubspotPipeline,
  FathomCall,
  PipelineSummary,
  WinLossStats,
  FunnelStats,
  ClosedRevenueStats,
  VisitorStats,
  PipelineMetric,
  RevenueForecast,
  EmailTemplate,
  EmailEvent,
  EmailSequence,
  SequenceEnrollment,
  SequenceStats,
  SequenceSchedulingSettings,
  EmailSequenceStep,
  EmailSequenceStepVariant,
  VariantStats,
  PubChannel,
  PubSchedule,
  PubPost,
  PubPostChannel,
  PubSettings,
  PubPostTemplate,
  SocialPostStatus,
} from '@/types';

const baseURL = import.meta.env.VITE_SALES_API_URL || '/api/v1';

export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public API client — no auth token, no 401 redirect
const publicApiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach bearer token + active tenant
api.interceptors.request.use((config) => {
  const { accessToken, activeOrgId } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (activeOrgId) {
    config.headers['X-Org-Id'] = activeOrgId;
  }
  return config;
});

// Single-flight token refresh — concurrent 401s share one refresh call so we
// never burn the same (rotating) refresh token twice.
let refreshPromise: Promise<string | null> | null = null;

function tryRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { refreshToken } = useAuthStore.getState();
      if (!refreshToken) return null;
      const { refreshAccessToken } = await import('./auth-callback');
      const refreshed = await refreshAccessToken(refreshToken);
      if (!refreshed) return null;
      useAuthStore
        .getState()
        .setTokens(refreshed.accessToken, refreshed.refreshToken ?? refreshToken);
      return refreshed.accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Session is gone — bounce through OIDC login and come back to the CURRENT
// page. Previously this dumped users at the public root, which left anyone on
// an /admin deep link stranded on the intake form when their token expired.
async function redirectToLogin() {
  useAuthStore.getState().clearAuth();
  const { getAuthLoginUrl } = await import('./auth-callback');
  const url = await getAuthLoginUrl(window.location.href);
  window.location.href = url;
}

// Handle 401 responses — refresh the access token and retry once; only
// re-authenticate if the refresh itself fails.
async function handle401(error: any, client: typeof api) {
  const original = error.config;
  if (original && !original._retry) {
    original._retry = true;
    const newToken = await tryRefresh();
    if (newToken) {
      original.headers = original.headers ?? {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return client(original);
    }
    await redirectToLogin();
  }
  return Promise.reject(error);
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      return handle401(error, api);
    }
    return Promise.reject(error);
  }
);

// Accounts API client (proxied through Sales API)
const accountsBaseURL = import.meta.env.VITE_ACCOUNTS_API_URL || '/accounts';

export const accountsApi = axios.create({
  baseURL: accountsBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

accountsApi.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

accountsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      return handle401(error, accountsApi);
    }
    return Promise.reject(error);
  }
);

// ============================================
// Custom Fields API
// ============================================

export const customFieldsApi = {
  list: (entityType?: string) =>
    api.get<CustomFieldDefinition[]>('/custom-fields', { params: entityType ? { entityType } : {} }).then((r) => r.data),

  create: (data: {
    label: string;
    name?: string;
    fieldType: string;
    entityType: string;
    options?: string[];
    isRequired?: boolean;
    description?: string;
    position?: number;
  }) =>
    api.post<CustomFieldDefinition>('/custom-fields', data).then((r) => r.data),

  update: (id: string, data: {
    label?: string;
    options?: string[];
    isRequired?: boolean;
    isActive?: boolean;
    description?: string;
  }) =>
    api.patch<CustomFieldDefinition>(`/custom-fields/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/custom-fields/${id}`).then(() => {}),

  reorder: (fieldIds: string[]) =>
    api.post<CustomFieldDefinition[]>('/custom-fields/reorder', { fieldIds }).then((r) => r.data),
};

// ============================================
// API Keys
// ============================================

import type { ApiKey, ApiKeyCreated } from '@/types';

export const apiKeysApi = {
  list: () =>
    api.get<ApiKey[]>('/api-keys').then((r) => r.data),

  create: (data: { name: string; expiresAt?: string }) =>
    api.post<ApiKeyCreated>('/api-keys', data).then((r) => r.data),

  revoke: (id: string) =>
    api.post<ApiKey>(`/api-keys/${id}/revoke`).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/api-keys/${id}`).then(() => {}),
};

// ============================================
// Contacts API
// ============================================

export interface CreateContactData {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
  linkedinUrl?: string;
  source?: string;
  status?: string;
  organizationId?: string;
  customFields?: Record<string, any>;
}

export const contactsApi = {
  list: (params?: {
    search?: string;
    status?: string;
    source?: string;
    organizationId?: string;
    take?: number;
    skip?: number;
  }) => api.get<PaginatedResponse<Contact>>('/contacts', { params }).then((r) => r.data),

  getById: (id: string) => api.get<Contact>(`/contacts/${id}`).then((r) => r.data),

  create: (data: CreateContactData) =>
    api.post<Contact>('/contacts', data).then((r) => r.data),

  update: (id: string, data: Partial<CreateContactData>) =>
    api.patch<Contact>(`/contacts/${id}`, data).then((r) => r.data),

  delete: (id: string) => api.delete(`/contacts/${id}`).then(() => {}),
};

// ============================================
// Organizations API
// ============================================

export interface CreateOrganizationData {
  name: string;
  domain?: string;
  website?: string;
  industry?: string;
  size?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  notes?: string;
  customFields?: Record<string, any>;
}

export const organizationsApi = {
  list: (params?: {
    search?: string;
    take?: number;
    skip?: number;
  }) => api.get<PaginatedResponse<Organization>>('/organizations', { params }).then((r) => r.data),

  getById: (id: string) => api.get<Organization>(`/organizations/${id}`).then((r) => r.data),

  create: (data: CreateOrganizationData) =>
    api.post<Organization>('/organizations', data).then((r) => r.data),

  update: (id: string, data: Partial<CreateOrganizationData>) =>
    api.patch<Organization>(`/organizations/${id}`, data).then((r) => r.data),

  delete: (id: string) => api.delete(`/organizations/${id}`).then(() => {}),
};

// ============================================
// Deals API
// ============================================

export const dealsApi = {
  list: (params?: {
    search?: string;
    stageId?: string;
    ownerId?: string;
    organizationId?: string;
    priority?: string;
    take?: number;
    skip?: number;
  }) => api.get<PaginatedResponse<Deal>>('/deals', { params }).then((r) => r.data),

  board: (pipelineId?: string, search?: string) =>
    api.get<BoardResponse>('/deals/board', { params: { pipelineId, search: search || undefined } }).then((r) => r.data),

  getById: (id: string) => api.get<Deal>(`/deals/${id}`).then((r) => r.data),

  create: (data: {
    title: string;
    value?: number;
    currency?: string;
    pipelineId?: string;
    stageId?: string;
    organizationId?: string;
    ownerId?: string;
    dueDate?: string;
    description?: string;
    priority?: string;
    contactIds?: string[];
    customFields?: Record<string, any>;
  }) => api.post<Deal>('/deals', data).then((r) => r.data),

  update: (id: string, data: {
    title?: string;
    value?: number;
    organizationId?: string;
    ownerId?: string;
    dueDate?: string;
    description?: string;
    priority?: string;
    customFields?: Record<string, any>;
  }) => api.patch<Deal>(`/deals/${id}`, data).then((r) => r.data),

  moveStage: (id: string, stageId: string) =>
    api.post<Deal>(`/deals/${id}/move`, { stageId }).then((r) => r.data),

  moveToPipeline: (id: string, pipelineId: string) =>
    api.post<Deal>(`/deals/${id}/move-pipeline`, { pipelineId }).then((r) => r.data),

  close: (id: string, data: { closeReasonId: string; closeNotes?: string }) =>
    api.post<Deal>(`/deals/${id}/close`, data).then((r) => r.data),

  reopen: (id: string) =>
    api.post<Deal>(`/deals/${id}/reopen`).then((r) => r.data),

  addContact: (dealId: string, contactId: string, role?: string) =>
    api.post(`/deals/${dealId}/contacts`, { contactId, role }).then((r) => r.data),

  removeContact: (dealId: string, contactId: string) =>
    api.delete(`/deals/${dealId}/contacts/${contactId}`).then(() => {}),

  delete: (id: string) => api.delete(`/deals/${id}`).then(() => {}),

  bulkDelete: (dealIds: string[]) =>
    api.post<{ deleted: number }>('/deals/bulk-delete', { dealIds }).then((r) => r.data),

  bulkMovePipeline: (dealIds: string[], targetPipelineId: string) =>
    api.post<{ moved: number }>('/deals/bulk-move-pipeline', { dealIds, targetPipelineId }).then((r) => r.data),

  // --- Proposed payment schedule (feeds the financials cash forecast) ---

  getPaymentSchedule: (dealId: string) =>
    api.get<PaymentSchedule | null>(`/deals/${dealId}/payment-schedule`).then((r) => r.data),

  savePaymentSchedule: (dealId: string, data: SavePaymentSchedulePayload) =>
    api.put<PaymentSchedule | null>(`/deals/${dealId}/payment-schedule`, data).then((r) => r.data),

  deletePaymentSchedule: (dealId: string) =>
    api.delete<{ deleted: boolean }>(`/deals/${dealId}/payment-schedule`).then((r) => r.data),
};

// ============================================
// Pipelines API
// ============================================

export const pipelinesApi = {
  list: () => api.get<Pipeline[]>('/pipelines').then((r) => r.data),

  getById: (id: string) => api.get<Pipeline>(`/pipelines/${id}`).then((r) => r.data),

  create: (data: { name: string }) =>
    api.post<Pipeline>('/pipelines', data).then((r) => r.data),

  update: (id: string, data: { name?: string; defaultOwnerId?: string | null }) =>
    api.patch<Pipeline>(`/pipelines/${id}`, data).then((r) => r.data),

  remove: (id: string) =>
    api.delete(`/pipelines/${id}`).then(() => {}),

  setDefault: (id: string) =>
    api.post<Pipeline>(`/pipelines/${id}/set-default`).then((r) => r.data),

  createStage: (pipelineId: string, data: {
    name: string;
    position: number;
    color?: string;
    winProbability?: number;
    isTerminal?: boolean;
    isWon?: boolean;
  }) => api.post(`/pipelines/${pipelineId}/stages`, data).then((r) => r.data),

  updateStage: (pipelineId: string, stageId: string, data: {
    name?: string;
    color?: string;
    winProbability?: number;
    isTerminal?: boolean;
    isWon?: boolean;
  }) => api.patch(`/pipelines/${pipelineId}/stages/${stageId}`, data).then((r) => r.data),

  deleteStage: (pipelineId: string, stageId: string) =>
    api.delete(`/pipelines/${pipelineId}/stages/${stageId}`).then(() => {}),

  reorderStages: (pipelineId: string, stageIds: string[]) =>
    api.post(`/pipelines/${pipelineId}/stages/reorder`, { stageIds }).then((r) => r.data),
};

// ============================================
// Close Reasons API
// ============================================

export const closeReasonsApi = {
  list: () => api.get<CloseReason[]>('/close-reasons').then((r) => r.data),

  create: (data: {
    label: string;
    description?: string;
    category: string;
    position: number;
  }) => api.post<CloseReason>('/close-reasons', data).then((r) => r.data),

  update: (id: string, data: {
    label?: string;
    description?: string;
    category?: string;
  }) => api.patch<CloseReason>(`/close-reasons/${id}`, data).then((r) => r.data),

  delete: (id: string) => api.delete(`/close-reasons/${id}`).then(() => {}),
};

// ============================================
// Notes API
// ============================================

export const notesApi = {
  list: (params?: {
    contactId?: string;
    organizationId?: string;
    dealId?: string;
  }) => api.get<Note[]>('/notes', { params }).then((r) => r.data),

  create: (data: {
    content: string;
    contactId?: string;
    organizationId?: string;
    dealId?: string;
  }) => api.post<Note>('/notes', data).then((r) => r.data),

  update: (id: string, data: { content: string }) =>
    api.patch<Note>(`/notes/${id}`, data).then((r) => r.data),

  togglePin: (id: string) =>
    api.post<Note>(`/notes/${id}/pin`).then((r) => r.data),

  delete: (id: string) => api.delete(`/notes/${id}`).then(() => {}),
};

// ============================================
// Activities API
// ============================================

export const activitiesApi = {
  list: (params?: {
    contactId?: string;
    organizationId?: string;
    dealId?: string;
    take?: number;
    skip?: number;
  }) => api.get<Activity[]>('/activities', { params }).then((r) => r.data),
};

// ============================================
// Tracking API
// ============================================

export const trackingApi = {
  listVisitors: (params?: {
    take?: number;
    skip?: number;
    identified?: boolean;
  }) => api.get<PaginatedResponse<Visitor>>('/tracking/visitors', { params }).then((r) => r.data),

  getVisitor: (visitorId: string) =>
    api.get<VisitorDetail>(`/tracking/visitors/${visitorId}`).then((r) => r.data),

  getContactPageViews: (contactId: string, params?: { take?: number; skip?: number }) =>
    api.get<PaginatedResponse<PageView>>(`/tracking/contacts/${contactId}/page-views`, { params }).then((r) => r.data),
};

// ============================================
// Forms API
// ============================================

export const formsApi = {
  list: (params?: {
    formType?: string;
    aiReviewStatus?: string;
    processed?: string;
    take?: number;
    skip?: number;
  }) => api.get<PaginatedResponse<FormSubmission>>('/forms/submissions', { params }).then((r) => r.data),

  getById: (id: string) =>
    api.get<FormSubmission>(`/forms/submissions/${id}`).then((r) => r.data),

  process: (id: string) =>
    api.post<{ deal: Deal; contactId: string }>(`/forms/submissions/${id}/process`).then((r) => r.data),

  remove: (id: string) =>
    api.delete(`/forms/submissions/${id}`).then((r) => r.data),
};

// ============================================
// Qualifying Questions API
// ============================================

export const qualifyingQuestionsApi = {
  list: () =>
    api.get<QualifyingQuestion[]>('/qualifying-questions').then((r) => r.data),

  create: (data: {
    question: string;
    formField?: string;
    importance?: string;
    helpText?: string;
    position: number;
    redFlagKeywords?: string[];
    greenFlagKeywords?: string[];
  }) => api.post<QualifyingQuestion>('/qualifying-questions', data).then((r) => r.data),

  update: (id: string, data: {
    question?: string;
    formField?: string;
    importance?: string;
    helpText?: string;
    isActive?: boolean;
    redFlagKeywords?: string[];
    greenFlagKeywords?: string[];
  }) => api.patch<QualifyingQuestion>(`/qualifying-questions/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/qualifying-questions/${id}`).then(() => {}),

  reorder: (questionIds: string[]) =>
    api.post<QualifyingQuestion[]>('/qualifying-questions/reorder', { questionIds }).then((r) => r.data),
};

// ============================================
// AI Agent API
// ============================================

export const aiAgentApi = {
  review: (submissionId: string) =>
    api.post<AiReviewResult>(`/ai-agent/review/${submissionId}`).then((r) => r.data),

  sendFollowUp: (submissionId: string) =>
    api.post<{ sent: boolean }>(`/ai-agent/follow-up/${submissionId}`).then((r) => r.data),
};

// ============================================
// LeadMagic Integration API
// ============================================

export const leadMagicApi = {
  getStatus: () =>
    api.get<{ isActive: boolean; metadata?: Record<string, any> } | null>('/integrations/leadmagic/status').then((r) => r.data),

  connect: (apiKey: string) =>
    api.post('/integrations/leadmagic/connect', { apiKey }).then((r) => r.data),

  disconnect: () =>
    api.delete('/integrations/leadmagic/disconnect').then(() => {}),
};

// ============================================
// Slack Integration API
// ============================================

export const slackApi = {
  getStatus: () =>
    api.get<{ isActive: boolean; metadata?: Record<string, any> } | null>('/integrations/slack/status').then((r) => r.data),

  connect: (data: { botToken: string; signingSecret: string; channelId: string }) =>
    api.post('/integrations/slack/connect', data).then((r) => r.data),

  disconnect: () =>
    api.delete('/integrations/slack/disconnect').then(() => {}),

  test: () =>
    api.post<{ ok: boolean }>('/integrations/slack/test').then((r) => r.data),

  listChannels: (botToken?: string) =>
    api.get<Array<{ id: string; name: string; isPrivate: boolean }>>('/integrations/slack/channels', {
      params: botToken ? { botToken } : undefined,
    }).then((r) => r.data),

  changeChannel: (channelId: string) =>
    api.post('/integrations/slack/change-channel', { channelId }).then((r) => r.data),
};

// ============================================
// Proposals API
// ============================================

export const proposalsApi = {
  list: (dealId: string) =>
    api.get<ProposalDocument[]>(`/proposals/deal/${dealId}`).then((r) => r.data),

  create: (data: { dealId: string; driveFileId: string; name: string; mimeType?: string; webViewLink?: string; webContentLink?: string }) =>
    api.post<ProposalDocument>('/proposals', data).then((r) => r.data),

  updateStatus: (id: string, status: string) =>
    api.patch<ProposalDocument>(`/proposals/${id}/status`, { status }).then((r) => r.data),

  delete: (id: string) => api.delete(`/proposals/${id}`).then(() => {}),

  createFolder: (dealId: string) =>
    api.post<{ folderId: string; webViewLink: string }>(`/proposals/deal/${dealId}/create-folder`).then((r) => r.data),

  listDriveFiles: (dealId: string) =>
    api.get<any[]>(`/proposals/deal/${dealId}/drive-files`).then((r) => r.data),
};

// ============================================
// Meetings API
// ============================================

export const meetingsApi = {
  list: (params?: { contactId?: string; dealId?: string; take?: number; skip?: number }) =>
    api.get<MeetingNote[]>('/meetings', { params }).then((r) => r.data),

  getById: (id: string) =>
    api.get<MeetingNote>(`/meetings/${id}`).then((r) => r.data),

  update: (id: string, data: { dealId?: string; contactId?: string }) =>
    api.patch<MeetingNote>(`/meetings/${id}`, data).then((r) => r.data),

  toggleActionItem: (id: string, index: number) =>
    api.post<MeetingNote>(`/meetings/${id}/action-items/${index}/toggle`).then((r) => r.data),

  // AI Association
  associate: (id: string, dealId: string) =>
    api.post<MeetingNote>(`/meetings/${id}/associate`, { dealId }).then((r) => r.data),

  acceptSuggestion: (id: string) =>
    api.post<MeetingNote>(`/meetings/${id}/accept-suggestion`).then((r) => r.data),

  rejectSuggestion: (id: string, dealId?: string) =>
    api.post<MeetingNote>(`/meetings/${id}/reject-suggestion`, { dealId }).then((r) => r.data),

  reassociate: (id: string) =>
    api.post<MeetingNote>(`/meetings/${id}/reassociate`).then((r) => r.data),

  // Fathom integration
  getFathomStatus: () =>
    api.get<IntegrationConnection | null>('/meetings/fathom/status').then((r) => r.data),

  connectFathom: (apiKey: string) =>
    api.post<IntegrationConnection>('/meetings/fathom/connect', { apiKey }).then((r) => r.data),

  disconnectFathom: () =>
    api.delete('/meetings/fathom/disconnect').then(() => {}),

  syncFathom: () =>
    api.post<{ synced: number }>('/meetings/fathom/sync').then((r) => r.data),

  listFathomCalls: (params?: { limit?: number; after?: string }) =>
    api.get<{ calls: FathomCall[]; hasMore: boolean }>('/meetings/fathom/calls', { params }).then((r) => r.data),
};

// ============================================
// Webhooks API
// ============================================

export const webhooksApi = {
  listEndpoints: () =>
    api.get<WebhookEndpoint[]>('/webhooks/endpoints').then((r) => r.data),

  createEndpoint: (data: { name: string; url: string; events: string[] }) =>
    api.post<WebhookEndpoint>('/webhooks/endpoints', data).then((r) => r.data),

  updateEndpoint: (id: string, data: { name?: string; url?: string; events?: string[]; isActive?: boolean }) =>
    api.patch<WebhookEndpoint>(`/webhooks/endpoints/${id}`, data).then((r) => r.data),

  deleteEndpoint: (id: string) =>
    api.delete(`/webhooks/endpoints/${id}`).then(() => {}),

  getDeliveries: (id: string) =>
    api.get<WebhookDelivery[]>(`/webhooks/endpoints/${id}/deliveries`).then((r) => r.data),

  testEndpoint: (id: string) =>
    api.post(`/webhooks/endpoints/${id}/test`).then((r) => r.data),

  listTokens: () =>
    api.get<InboundWebhookToken[]>('/webhooks/tokens').then((r) => r.data),

  createToken: (data: { name: string; source: string }) =>
    api.post<InboundWebhookToken>('/webhooks/tokens', data).then((r) => r.data),

  deleteToken: (id: string) =>
    api.delete(`/webhooks/tokens/${id}`).then(() => {}),
};

// ============================================
// HubSpot Import API
// ============================================

export const hubspotImportApi = {
  // CSV import (existing)
  list: () =>
    api.get<HubspotImport[]>('/hubspot-import').then((r) => r.data),

  getById: (id: string) =>
    api.get<HubspotImport>(`/hubspot-import/${id}`).then((r) => r.data),

  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<HubspotImportPreview>('/hubspot-import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  getPreview: (id: string) =>
    api.get<HubspotImportPreview>(`/hubspot-import/${id}/preview`).then((r) => r.data),

  submitMapping: (id: string, fieldMapping: Record<string, string>, pipelineId?: string) =>
    api.post<{ id: string; status: string }>(`/hubspot-import/${id}/mapping`, { fieldMapping, pipelineId }).then((r) => r.data),

  // OAuth connection
  getOAuthUrl: () =>
    api.get<{ url: string }>('/hubspot-import/oauth/authorize').then((r) => r.data),

  getOAuthStatus: () =>
    api.get<IntegrationConnection | null>('/hubspot-import/oauth/status').then((r) => r.data),

  disconnect: () =>
    api.delete('/hubspot-import/oauth/disconnect').then(() => {}),

  // HubSpot API browsing
  getApiDeals: (params?: { search?: string; after?: string; limit?: number; status?: string; pipeline?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<{ deals: HubspotDeal[]; paging?: { next?: { after: string } } }>('/hubspot-import/api/deals', {
      params: {
        query: params?.search,
        limit: params?.limit,
        after: params?.after,
        status: params?.status,
        pipeline: params?.pipeline,
        dateFrom: params?.dateFrom,
        dateTo: params?.dateTo,
      },
    }).then((r) => r.data),

  getApiPipelines: () =>
    api.get<HubspotPipeline[]>('/hubspot-import/api/pipelines').then((r) => r.data),

  importApiDeals: (data: { dealIds: string[]; stageMapping: Record<string, string>; pipelineId?: string }) =>
    api.post<{ imported: number; errors: string[] }>('/hubspot-import/api/import', data).then((r) => r.data),

  // HubSpot contacts browsing + import
  getApiContacts: (params?: { search?: string; after?: string; limit?: number }) =>
    api.get<{ contacts: HubspotContactType[]; paging?: { next?: { after: string } } }>('/hubspot-import/api/contacts', {
      params: {
        query: params?.search,
        limit: params?.limit,
        after: params?.after,
      },
    }).then((r) => r.data),

  importApiContacts: (data: { contactIds: string[]; importLinkedCompanies?: boolean }) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>('/hubspot-import/api/import-contacts', data).then((r) => r.data),

  // HubSpot companies browsing + import
  getApiCompanies: (params?: { search?: string; after?: string; limit?: number }) =>
    api.get<{ companies: HubspotCompanyType[]; paging?: { next?: { after: string } } }>('/hubspot-import/api/companies', {
      params: {
        query: params?.search,
        limit: params?.limit,
        after: params?.after,
      },
    }).then((r) => r.data),

  importApiCompanies: (data: { companyIds: string[] }) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>('/hubspot-import/api/import-companies', data).then((r) => r.data),
};

// ============================================
// Conversion API
// ============================================

export const conversionApi = {
  convert: (dealId: string, data: {
    createOrg: boolean;
    orgName?: string;
    inviteContacts: string[];
    createHubProject: boolean;
    hubProjectName?: string;
  }) => api.post<{
    ecosystemOrgId?: string;
    ecosystemProjectId?: string;
    invitedContacts: number;
  }>(`/conversion/deals/${dealId}/convert`, data).then((r) => r.data),
};

// ============================================
// Dashboard API
// ============================================

export const dashboardApi = {
  getPipelineSummary: () =>
    api.get<PipelineSummary[]>('/dashboard/pipeline-summary').then((r) => r.data),

  getRecentActivity: (params?: { take?: number }) =>
    api.get<Activity[]>('/dashboard/recent-activity', { params }).then((r) => r.data),

  getUpcomingCloses: (params?: { take?: number }) =>
    api.get<Deal[]>('/dashboard/upcoming-closes', { params }).then((r) => r.data),

  getWinLossStats: () =>
    api.get<WinLossStats>('/dashboard/win-loss-stats').then((r) => r.data),

  getFunnel: () =>
    api.get<FunnelStats>('/dashboard/funnel').then((r) => r.data),

  getClosedRevenue: () =>
    api.get<ClosedRevenueStats>('/dashboard/closed-revenue').then((r) => r.data),

  getVisitorStats: () =>
    api.get<VisitorStats>('/dashboard/visitor-stats').then((r) => r.data),

  getPipelineMetrics: (pipelineId?: string) =>
    api.get<PipelineMetric[]>('/dashboard/pipeline-metrics', { params: pipelineId ? { pipelineId } : {} }).then((r) => r.data),

  getRevenueForecast: () =>
    api.get<RevenueForecast>('/dashboard/revenue-forecast').then((r) => r.data),
};

// ============================================
// Team API
// ============================================

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: string;
}

export const teamApi = {
  getMembers: () =>
    api.get<TeamMember[]>('/team/members').then((r) => r.data),
};

// ============================================
// Public Wizard API (no auth required)
// ============================================

export const wizardApi = {
  submitIntake: (data: {
    formType: 'RFP' | 'CONTACT';
    formData: Record<string, any>;
    hasDocument?: boolean;
    visitorId?: string;
    sourceUrl?: string;
  }) => publicApiClient.post('/forms/submit', data).then((r) => r.data),

  getSubmission: (id: string) =>
    publicApiClient.get(`/forms/submissions/${id}/public`).then((r) => r.data),

  uploadDocument: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return publicApiClient.post(`/forms/submissions/${id}/upload`, formData, {
      headers: { 'Content-Type': undefined },
    }).then((r) => r.data);
  },

  analyzeDocument: (id: string) =>
    publicApiClient.post(`/forms/submissions/${id}/analyze`).then((r) => r.data),

  getQuestions: (id: string) =>
    publicApiClient.get(`/forms/submissions/${id}/questions`).then((r) => r.data),

  submitAnswers: (id: string, answers: Array<{ questionId: string; answer: string }>) =>
    publicApiClient.post(`/forms/submissions/${id}/answers`, { answers }).then((r) => r.data),

  setDealType: (id: string, dealTypeId: string) =>
    publicApiClient.post(`/forms/submissions/${id}/deal-type`, { dealTypeId }).then((r) => r.data),

  getDealTypes: () =>
    publicApiClient.get('/deal-types/public').then((r) => r.data),

  getQualifyingQuestions: () =>
    publicApiClient.get('/qualifying-questions/public').then((r) => r.data),

  updateMetadata: (id: string, metadata: Record<string, string>) =>
    publicApiClient.patch(`/forms/submissions/${id}/metadata`, metadata).then((r) => r.data),

  uploadAdditionalDoc: (id: string, file: File, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    return publicApiClient.post(`/forms/submissions/${id}/documents`, formData, {
      headers: { 'Content-Type': undefined },
    }).then((r) => r.data);
  },

  listDocuments: (id: string) =>
    publicApiClient.get(`/forms/submissions/${id}/documents`).then((r) => r.data),

  removeDocument: (id: string, docId: string) =>
    publicApiClient.delete(`/forms/submissions/${id}/documents/${docId}`).then((r) => r.data),

  // Multi-type selection
  selectDealTypes: (id: string, dealTypeIds: string[], proposalPreference?: 'COMBINED' | 'SEPARATE') =>
    publicApiClient.post(`/forms/submissions/${id}/select-types`, {
      dealTypeIds,
      proposalPreference,
    }).then((r) => r.data),

  // Per-type questions
  getQuestionsForTypes: (id: string, dealTypeIds: string[]) =>
    publicApiClient.post(`/forms/submissions/${id}/questions-for-types`, { dealTypeIds }).then((r) => r.data),

  // Template variables
  getTemplateVariables: (dealTypeId: string) =>
    publicApiClient.get(`/forms/deal-types/${dealTypeId}/template-variables`).then((r) => r.data),

  saveTemplateVariables: (id: string, variables: Record<string, string>) =>
    publicApiClient.post(`/forms/submissions/${id}/template-variables`, { variables }).then((r) => r.data),

  // AI SDR Evaluation
  evaluate: (id: string) =>
    publicApiClient.post(`/forms/submissions/${id}/evaluate`).then((r) => r.data),

  submitClarifyingAnswers: (id: string, answers: Record<string, string>) =>
    publicApiClient.post(`/forms/submissions/${id}/clarify`, { answers }).then((r) => r.data),
};

// ============================================
// Deal Types API (authenticated)
// ============================================

export const dealTypesApi = {
  list: () =>
    api.get('/deal-types').then((r) => r.data),

  getById: (id: string) =>
    api.get(`/deal-types/${id}`).then((r) => r.data),

  create: (data: { name: string; description?: string; icon?: string; position: number; templateDocId?: string; templateFolderId?: string }) =>
    api.post('/deal-types', data).then((r) => r.data),

  update: (id: string, data: { name?: string; description?: string; icon?: string; position?: number; isActive?: boolean; templateDocId?: string; templateFolderId?: string }) =>
    api.patch(`/deal-types/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/deal-types/${id}`).then(() => {}),

  reorder: (dealTypeIds: string[]) =>
    api.post('/deal-types/reorder', { dealTypeIds }).then((r) => r.data),

  // Question association
  getQuestions: (id: string) =>
    api.get(`/deal-types/${id}/questions`).then((r) => r.data),

  setQuestions: (id: string, questionIds: string[]) =>
    api.put(`/deal-types/${id}/questions`, { questionIds }).then((r) => r.data),
};

// ============================================
// ICP (Ideal Customer Profile) API
// ============================================

export const icpApi = {
  get: () =>
    api.get('/icp').then((r) => r.data),

  update: (id: string, data: {
    name?: string;
    markdownContent?: string;
  }) =>
    api.put(`/icp/${id}`, data).then((r) => r.data),
};

// ============================================
// Email Templates API
// ============================================

export const emailTemplatesApi = {
  list: () =>
    api.get<EmailTemplate[]>('/email-templates').then((r) => r.data),

  listEvents: () =>
    api.get<EmailEvent[]>('/email-templates/events').then((r) => r.data),

  getByEvent: (event: string) =>
    api.get<EmailTemplate>(`/email-templates/${event}`).then((r) => r.data),

  update: (event: string, data: {
    subject?: string;
    body?: string;
    designJson?: any;
    isActive?: boolean;
    replyTo?: string;
  }) =>
    api.patch<EmailTemplate>(`/email-templates/${event}`, data).then((r) => r.data),

  resetToDefault: (event: string) =>
    api.post<EmailTemplate>(`/email-templates/${event}/reset`).then((r) => r.data),
};

// ============================================
// SDR Profile (AI SDR Training) API
// ============================================

export const sdrProfileApi = {
  get: () =>
    api.get('/sdr-profile').then((r) => r.data),

  update: (id: string, data: {
    companyName?: string;
    identityMarkdown?: string;
    customInstructionsMarkdown?: string;
  }) =>
    api.put(`/sdr-profile/${id}`, data).then((r) => r.data),
};

// ============================================
// Email Sequences API
// ============================================

export const sequencesApi = {
  list: (params?: { status?: string }) =>
    api.get<EmailSequence[]>('/sequences', { params }).then((r) => r.data),

  getById: (id: string) =>
    api.get<EmailSequence>(`/sequences/${id}`).then((r) => r.data),

  create: (data: {
    name: string;
    description?: string;
    steps?: Array<{
      position: number;
      subject: string;
      body: string;
      delayDays?: number;
      delayHours?: number;
      replyTo?: boolean;
    }>;
  }) =>
    api.post<EmailSequence>('/sequences', data).then((r) => r.data),

  update: (id: string, data: { name?: string; description?: string; bccEnabled?: boolean; bccEmails?: string[] }) =>
    api.patch<EmailSequence>(`/sequences/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/sequences/${id}`).then(() => {}),

  // Steps
  addStep: (sequenceId: string, data: {
    position: number;
    subject: string;
    body: string;
    delayDays?: number;
    delayHours?: number;
    replyTo?: boolean;
  }) =>
    api.post<EmailSequenceStep>(`/sequences/${sequenceId}/steps`, data).then((r) => r.data),

  updateStep: (stepId: string, data: {
    subject?: string;
    body?: string;
    delayDays?: number;
    delayHours?: number;
    replyTo?: boolean;
  }) =>
    api.patch<EmailSequenceStep>(`/sequences/steps/${stepId}`, data).then((r) => r.data),

  removeStep: (stepId: string) =>
    api.delete(`/sequences/steps/${stepId}`).then(() => {}),

  sendTestEmail: (stepId: string, recipientEmail: string, testVariables?: Record<string, string>, variantId?: string) =>
    api.post<{ success: boolean }>(`/sequences/steps/${stepId}/test-email`, { recipientEmail, ...(testVariables && Object.keys(testVariables).length > 0 ? { testVariables } : {}), ...(variantId ? { variantId } : {}) }).then((r) => r.data),

  // Variants
  addVariant: (stepId: string, data: { subject: string; body: string; weight?: number }) =>
    api.post<EmailSequenceStepVariant>(`/sequences/steps/${stepId}/variants`, data).then((r) => r.data),

  updateVariant: (variantId: string, data: { subject?: string; body?: string; weight?: number }) =>
    api.patch<EmailSequenceStepVariant>(`/sequences/variants/${variantId}`, data).then((r) => r.data),

  removeVariant: (variantId: string) =>
    api.delete(`/sequences/variants/${variantId}`).then(() => {}),

  getVariantStats: (stepId: string) =>
    api.get<VariantStats[]>(`/sequences/steps/${stepId}/variant-stats`).then((r) => r.data),

  // Enrollment → Deal
  createDealFromEnrollment: (enrollmentId: string, data: { title: string; value?: number }) =>
    api.post<Deal>(`/sequences/enrollments/${enrollmentId}/deal`, data).then((r) => r.data),

  reorderSteps: (sequenceId: string, stepIds: string[]) =>
    api.post(`/sequences/${sequenceId}/steps/reorder`, { stepIds }).then((r) => r.data),

  sendOneNow: (executionId: string) =>
    api.post<{ sent: boolean }>(`/sequences/executions/${executionId}/send-now`).then((r) => r.data),

  // Status
  approve: (id: string) =>
    api.post<EmailSequence>(`/sequences/${id}/approve`).then((r) => r.data),

  pause: (id: string) =>
    api.post<EmailSequence>(`/sequences/${id}/pause`).then((r) => r.data),

  resume: (id: string) =>
    api.post<EmailSequence>(`/sequences/${id}/resume`).then((r) => r.data),

  archive: (id: string) =>
    api.post<EmailSequence>(`/sequences/${id}/archive`).then((r) => r.data),

  // Enrollment
  enroll: (id: string, data: { contactIds?: string[]; newContacts?: Array<{ email: string; firstName: string; lastName: string; jobTitle?: string; organizationName?: string }>; dealId?: string; variables?: Record<string, string>; bccEmails?: string[] }) =>
    api.post<{ enrolled: number; contactIds: string[] }>(`/sequences/${id}/enroll`, data).then((r) => r.data),

  updateEnrollmentVariables: (enrollmentId: string, variables: Record<string, string>) =>
    api.patch<SequenceEnrollment>(`/sequences/enrollments/${enrollmentId}/variables`, { variables }).then((r) => r.data),

  unenroll: (id: string, contactId: string) =>
    api.delete(`/sequences/${id}/enroll/${contactId}`).then(() => {}),

  getEnrollments: (id: string) =>
    api.get<SequenceEnrollment[]>(`/sequences/${id}/enrollments`).then((r) => r.data),

  getStats: (id: string) =>
    api.get<SequenceStats>(`/sequences/${id}/stats`).then((r) => r.data),

  // Scheduling Settings
  getSettings: () =>
    api.get<SequenceSchedulingSettings>('/sequences/settings').then((r) => r.data),

  updateSettings: (data: Partial<Omit<SequenceSchedulingSettings, 'id'>>) =>
    api.patch<SequenceSchedulingSettings>('/sequences/settings', data).then((r) => r.data),

  // Gmail OAuth
  getGmailAuthUrl: () =>
    api.get<{ url: string }>('/sequences/gmail/authorize').then((r) => r.data),

  getGmailStatus: () =>
    api.get<{ isActive: boolean; metadata?: Record<string, any> } | null>('/sequences/gmail/status').then((r) => r.data),

  disconnectGmail: () =>
    api.delete('/sequences/gmail/disconnect').then(() => {}),
};

// ============================================
// Social Media (Publer) API
// ============================================

export const socialApi = {
  // Channels
  listChannels: () =>
    api.get<PubChannel[]>('/social/channels').then((r) => r.data),

  syncChannels: () =>
    api.post<PubChannel[]>('/social/channels/sync').then((r) => r.data),

  updateChannel: (id: string, data: { isActive?: boolean }) =>
    api.patch<PubChannel>(`/social/channels/${id}`, data).then((r) => r.data),

  // Schedules
  getSchedule: (channelId: string) =>
    api.get<PubSchedule>(`/social/channels/${channelId}/schedule`).then((r) => r.data),

  updateSchedule: (channelId: string, data: Partial<PubSchedule>) =>
    api.patch<PubSchedule>(`/social/channels/${channelId}/schedule`, data).then((r) => r.data),

  // Queue
  getQueue: (channelId: string) =>
    api.get<PubPostChannel[]>(`/social/channels/${channelId}/queue`).then((r) => r.data),

  // Posts
  listPosts: (params?: { status?: SocialPostStatus; channelId?: string; take?: number; skip?: number }) =>
    api.get<{ data: PubPost[]; total: number }>('/social/posts', { params }).then((r) => r.data),

  createPost: (data: { content: { default: string; overrides?: Record<string, string> }; channelIds: string[]; mediaUrls?: string[]; metadata?: Record<string, any> }) =>
    api.post<PubPost>('/social/posts', data).then((r) => r.data),

  getPost: (id: string) =>
    api.get<PubPost>(`/social/posts/${id}`).then((r) => r.data),

  updatePost: (id: string, data: any) =>
    api.patch<PubPost>(`/social/posts/${id}`, data).then((r) => r.data),

  deletePost: (id: string) =>
    api.delete(`/social/posts/${id}`).then(() => {}),

  cancelPost: (id: string) =>
    api.post<PubPost>(`/social/posts/${id}/cancel`).then((r) => r.data),

  retryPost: (id: string) =>
    api.post<PubPost>(`/social/posts/${id}/retry`).then((r) => r.data),

  // Settings
  getSettings: () =>
    api.get<PubSettings>('/social/settings').then((r) => r.data),

  updateSettings: (data: Partial<PubSettings>) =>
    api.patch<PubSettings>('/social/settings', data).then((r) => r.data),

  testConnection: () =>
    api.post<{ success: boolean; channels: number }>('/social/settings/test').then((r) => r.data),

  // Templates
  listTemplates: () =>
    api.get<PubPostTemplate[]>('/social/templates').then((r) => r.data),
  getTemplate: (id: string) =>
    api.get<PubPostTemplate>(`/social/templates/${id}`).then((r) => r.data),
  createTemplate: (data: Partial<PubPostTemplate>) =>
    api.post<PubPostTemplate>('/social/templates', data).then((r) => r.data),
  updateTemplate: (id: string, data: Partial<PubPostTemplate>) =>
    api.patch<PubPostTemplate>(`/social/templates/${id}`, data).then((r) => r.data),
  deleteTemplate: (id: string) =>
    api.delete(`/social/templates/${id}`).then(() => {}),
  createPostFromTemplate: (templateId: string, data: { variables: Record<string, string>; channelIds?: string[] }) =>
    api.post<PubPost>(`/social/templates/${templateId}/post`, data).then((r) => r.data),
  previewTemplate: (templateId: string, variables: Record<string, string>) =>
    api.get(`/social/templates/${templateId}/preview`, { params: { variables: JSON.stringify(variables) } }).then((r) => r.data),
};

// ============================================
// Send Templates API (User Email Templates)
// ============================================

export interface SendTemplate {
  id: string;
  userId: string;
  name: string;
  subject: string;
  body: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SentEmail {
  id: string;
  templateId?: string;
  userId: string;
  contactId: string;
  dealId?: string;
  subject: string;
  body: string;
  gmailMsgId?: string;
  trackingCode?: string;
  openedAt?: string;
  openCount: number;
  clickedAt?: string;
  clickCount: number;
  sentAt: string;
  contact?: { firstName: string; lastName: string; email: string };
  user?: { name: string };
  deal?: { title: string };
}

export const sendTemplatesApi = {
  list: () =>
    api.get<SendTemplate[]>('/send-templates').then((r) => r.data),

  get: (id: string) =>
    api.get<SendTemplate>(`/send-templates/${id}`).then((r) => r.data),

  create: (data: { name: string; subject: string; body: string }) =>
    api.post<SendTemplate>('/send-templates', data).then((r) => r.data),

  update: (id: string, data: Partial<{ name: string; subject: string; body: string; isActive: boolean }>) =>
    api.patch<SendTemplate>(`/send-templates/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/send-templates/${id}`).then(() => {}),

  seedDefaults: () =>
    api.post('/send-templates/seed-defaults').then(() => {}),

  send: (data: { templateId?: string; contactId: string; dealId?: string; subject?: string; body?: string }) =>
    api.post<{ id: string; trackingCode: string; gmailMsgId: string }>('/send-templates/send', data).then((r) => r.data),

  draft: (data: { contactId: string; dealId: string; emailType: string; customPrompt?: string }) =>
    api.post<{ subject: string; body: string }>('/send-templates/draft', data).then((r) => r.data),

  render: (data: { contactId: string; dealId: string; templateId?: string; subject?: string; body?: string }) =>
    api.post<{ subject: string; body: string }>('/send-templates/render', data).then((r) => r.data),

  test: (data: { templateId?: string; contactId: string; dealId?: string; subject?: string; body?: string }) =>
    api.post<{ ok: boolean; gmailMsgId?: string }>('/send-templates/test', data).then((r) => r.data),

  listSentForDeal: (dealId: string) =>
    api.get<SentEmail[]>(`/send-templates/sent/deal/${dealId}`).then((r) => r.data),

  listSentForContact: (contactId: string) =>
    api.get<SentEmail[]>(`/send-templates/sent/contact/${contactId}`).then((r) => r.data),

  getCalendarLink: () =>
    api.get<string | null>('/send-templates/calendar-link').then((r) => r.data),

  updateCalendarLink: (calendarLink: string) =>
    api.patch<{ calendarLink: string }>('/send-templates/calendar-link', { calendarLink }).then((r) => r.data),
};

// ============================================
// Notifications API
// ============================================

export interface NotificationConfig {
  id: string;
  event: string;
  channels: {
    slack: boolean;
    email: string[];
    sms: string[];
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const notificationsApi = {
  getConfigs: () =>
    api.get<NotificationConfig[]>('/notifications/config').then((r) => r.data),

  updateConfig: (event: string, data: { channels?: Partial<NotificationConfig['channels']>; isActive?: boolean }) =>
    api.patch<NotificationConfig>(`/notifications/config/${event}`, data).then((r) => r.data),
};
